import { memo } from 'react';
import { Search, X, User, Hash } from 'lucide-react';
import { AppealFilters as AppealFiltersType, APPEAL_STATUSES } from '../../types/appeals';
import { cn } from '../../utils/cn';
import { PlayerProfile } from '../PlayerProfile';

interface AppealFiltersProps {
  filters: AppealFiltersType;
  onFiltersChange: (filters: AppealFiltersType) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

export const AppealFilters = memo(({ filters, onFiltersChange, searchTerm, onSearchChange }: AppealFiltersProps) => {
  const hasActiveFilters = filters.status || filters.sort_by || searchTerm || filters.user_uuid || filters.ban_id;

  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      status: '',
      sort_by: '',
      sort_order: '',
      user_uuid: undefined,
      ban_id: undefined,
    });
    onSearchChange('');
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
          <input
            className="glass-input w-full pl-9"
            placeholder="Search by username, UUID, or ban ID…"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        <select
          className="glass-select"
          value={filters.status || ''}
          onChange={(e) => onFiltersChange({ ...filters, status: e.target.value as AppealFiltersType['status'], page: 1 })}
        >
          <option value="">All Statuses</option>
          {APPEAL_STATUSES.map(s => (
            <option key={s} value={s}>
              {s.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}
            </option>
          ))}
        </select>

        <select
          className="glass-select"
          value={filters.sort_by || ''}
          onChange={(e) => onFiltersChange({ ...filters, sort_by: e.target.value as AppealFiltersType['sort_by'] })}
        >
          <option value="">Default Sort</option>
          <option value="created_at">Created At</option>
          <option value="updated_at">Updated At</option>
          <option value="status">Status</option>
        </select>

        {filters.sort_by && (
          <select
            className="glass-select"
            value={filters.sort_order || 'asc'}
            onChange={(e) => onFiltersChange({ ...filters, sort_order: e.target.value as AppealFiltersType['sort_order'] })}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        )}
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap gap-1.5 items-center">
          {filters.user_uuid && (
            <span className="chip chip-removable">
              <User className="w-3 h-3 text-green-400" />
              Player: <PlayerProfile uuid={filters.user_uuid} inline showUuid={false} showAvatar={false} />
              <button onClick={() => onFiltersChange({ ...filters, user_uuid: undefined, page: 1 })} title="Remove player filter" aria-label="Remove player filter">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.ban_id && (
            <span className="chip chip-removable">
              <Hash className="w-3 h-3 text-red-400" />
              Ban ID: <span className="font-mono">{filters.ban_id}</span>
              <button onClick={() => onFiltersChange({ ...filters, ban_id: undefined, page: 1 })} title="Remove ban filter" aria-label="Remove ban filter">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {filters.status && (
            <span className="chip chip-removable capitalize">
              Status: {filters.status.replace(/_/g, ' ')}
              <button onClick={() => onFiltersChange({ ...filters, status: '', page: 1 })} title="Remove status filter" aria-label="Remove status filter">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          {searchTerm && (
            <span className="chip chip-removable">
              Search: {searchTerm}
              <button onClick={() => onSearchChange('')} title="Clear search" aria-label="Clear search">
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
          <button
            onClick={clearFilters}
            className={cn('btn-ghost btn-xs')}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
});
